import type { Directive } from 'vue';

type PrefetchFn = () => void;

// Debounced so sweeping the pointer across a list doesn't fire a fetch per row.
const DEBOUNCE_MS = 75;

type ElementState = { fn: PrefetchFn | undefined; timer: number | undefined };
const states = new WeakMap<HTMLElement, ElementState>();

function start(el: HTMLElement) {
  const state = states.get(el);
  if (!state || state.timer !== undefined) return;
  state.timer = window.setTimeout(() => {
    state.timer = undefined;
    state.fn?.();
  }, DEBOUNCE_MS);
}

function cancel(el: HTMLElement) {
  const state = states.get(el);
  if (!state) return;
  window.clearTimeout(state.timer);
  state.timer = undefined;
}

// Usage: v-prefetch="() => useProducts().prefetchProduct(id)"
// Fires the callback when the user shows intent (hover/focus), so the data is typically
// loaded by the time the click lands. May fire repeatedly for the same element — point it
// at idempotent prefetchers (cache-checked + request-deduped) only.
export const vPrefetch: Directive<HTMLElement, PrefetchFn | undefined> = {
  mounted(el, binding) {
    states.set(el, { fn: binding.value, timer: undefined });
    el.addEventListener('mouseenter', () => start(el));
    el.addEventListener('focusin', () => start(el));
    el.addEventListener('mouseleave', () => cancel(el));
  },
  updated(el, binding) {
    const state = states.get(el);
    if (state) state.fn = binding.value;
  },
  unmounted(el) {
    cancel(el);
    states.delete(el);
  },
};
